// src/frontend/components/room/ShareLink.jsx
import React, {useEffect, useRef, useState} from 'react';
import {BsCheckCircle, BsClipboard, BsLink45Deg, BsPersonPlus, BsThreeDotsVertical} from 'react-icons/bs';
import {useRoom} from '../../context/RoomContext';
import toast from 'react-hot-toast';

export default function ShareLink() {
    const {roomId, username} = useRoom();
    const [shareLink] = useState(`${window.location.origin}?room=${encodeURIComponent(roomId)}`);
    const [copied, setCopied] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [showNameModal, setShowNameModal] = useState(false);
    const [customName, setCustomName] = useState(username || '');

    // Refs für Click-Outside
    const dropdownRef = useRef(null);
    const modalRef = useRef(null);

    const copyToClipboard = (link) => {
        navigator.clipboard.writeText(link);
        setCopied(true);
        toast.success('Link kopiert!');
        setTimeout(() => setCopied(false), 2000);
    };

    const handleQuickCopy = () => {
        const customLink = `${window.location.origin}?room=${encodeURIComponent(roomId)}&quickjoin`;
        copyToClipboard(customLink);
        setShowDropdown(false);
    };

    const handleCustomCopy = () => {
        if (!customName.trim()) {
            toast.error('Bitte einen Namen eingeben!');
            return;
        }
        const customLink = `${window.location.origin}?room=${encodeURIComponent(roomId)}&user=${encodeURIComponent(customName.trim())}&quickjoin`;
        copyToClipboard(customLink);
        setShowNameModal(false);
        setShowDropdown(false);
    };

    // Click-Outside für Dropdown
    useEffect(() => {
        const handleClickOutsideDropdown = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };

        if (showDropdown) {
            document.addEventListener('mousedown', handleClickOutsideDropdown);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutsideDropdown);
        };
    }, [showDropdown]);

    // Click-Outside für Modal
    useEffect(() => {
        const handleClickOutsideModal = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                setShowNameModal(false);
            }
        };

        if (showNameModal) {
            document.addEventListener('mousedown', handleClickOutsideModal);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutsideModal);
        };
    }, [showNameModal]);

    return (
        <div
            className="bg-white/80 dark:bg-white/5 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-8 relative z-[1000]">
            <div className="flex items-center gap-3 mb-6">
                <BsLink45Deg className="text-3xl text-blue-500"/>
                <h2 className="text-2xl font-bold">Teile diesen Raum</h2>
            </div>

            <div className="flex gap-3">
                <input
                    type="text"
                    readOnly
                    value={shareLink}
                    className="flex-1 px-4 py-3 rounded-xl bg-gray-100 dark:bg-white/10 border border-gray-300 dark:border-white/20 font-mono text-sm"
                />

                {/* Kopieren-Button */}
                <button
                    onClick={() => copyToClipboard(shareLink)}
                    className={`px-6 py-3 rounded-xl font-semibold flex items-center gap-2 transition-all ${
                        copied ? 'bg-green-500 text-white' : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                >
                    {copied ? (
                        <>
                            <BsCheckCircle/> Kopiert!
                        </>
                    ) : (
                        <>
                            <BsClipboard/> Kopieren
                        </>
                    )}
                </button>

                {/* Drei-Punkte-Menü */}
                <div className="relative z-[1000]" ref={dropdownRef}>
                    <button
                        onClick={() => setShowDropdown(!showDropdown)}
                        className="p-3 rounded-xl bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                        aria-label="Mehr Optionen"
                    >
                        <BsThreeDotsVertical className="text-xl text-gray-700 dark:text-gray-300"/>
                    </button>

                    {showDropdown && (
                        <div
                            className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden z-[2000]">
                            <button
                                onClick={handleQuickCopy}
                                className="w-full px-4 py-3 text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-3"
                            >
                                <BsLink45Deg className="text-blue-500"/>
                                QuickLink kopieren (ohne Name)
                            </button>
                            <button
                                onClick={() => {
                                    setShowDropdown(false);
                                    setShowNameModal(true);
                                }}
                                className="w-full px-4 py-3 text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-3 border-t border-gray-200 dark:border-gray-700"
                            >
                                <BsPersonPlus className="text-green-500"/>
                                Mit Namen teilen...
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal für Namen eingeben */}
            {showNameModal && (
                <div
                    className="fixed inset-0 bg-black/60 flex items-center justify-center z-[3000]"
                    onClick={() => setShowNameModal(false)}
                >
                    <div
                        className="bg-white dark:bg-gray-800 rounded-2xl p-8 w-full max-w-md mx-4 shadow-2xl"
                        ref={modalRef}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 className="text-xl font-bold mb-6 text-gray-800 dark:text-white">
                            Link mit Namen teilen
                        </h3>

                        <input
                            type="text"
                            value={customName}
                            onChange={(e) => setCustomName(e.target.value)}
                            placeholder="z. B. Sven"
                            className="w-full px-4 py-3 rounded-xl bg-gray-100 dark:bg-white/10 border border-gray-300 dark:border-white/20 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 transition-all outline-none mb-6"
                        />

                        <div className="flex gap-4">
                            <button
                                onClick={() => setShowNameModal(false)}
                                className="flex-1 py-3 rounded-xl bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                            >
                                Abbrechen
                            </button>
                            <button
                                onClick={handleCustomCopy}
                                className="flex-1 py-3 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold hover:from-blue-700 hover:to-purple-700 transition-colors"
                            >
                                Link kopieren
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}